import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { isBedbankOffer, isBundleOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import {
  getBedbankAndFlightsChildMaxAge,
  getOfferAndFlightsMaxChildAge,
  getOfferMaxInfantAge,
  getTourV2OfferMaxChildAge,
  getTourV2OfferMinAge,
  getBundleAndSaveMaxChildAge,
  getBundleAndSaveMaxInfantAge,
  isSalesforceTourV2,
  getOfferMaxTeenagerAge,
} from 'lib/offer/offerUtils'
import { generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import OccupancyPickerVertical from 'components/Common/OccupancyPicker/OccupancyPickerVertical'
import noop from 'lib/function/noop'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'

interface Props {
  rooms?: Array<App.RoomOccupants>
  offer: App.Offer | App.BedbankOffer | App.Tours.TourV2Offer
  open?: boolean
  capacities?: Array<App.RoomCapacity>
  childrenAgeLabel?: string
  className?: string
  disableMultiRoom?: boolean
  onChange?: (occupants: Array<App.Occupants | App.RoomOccupants>) => void
}

const defaultRooms = [generateOfferPageRoom()]

function OfferCapacityWithAgesVerticalSelection(props: Props) {
  const {
    rooms = defaultRooms,
    offer,
    capacities,
    childrenAgeLabel: childrenAgeLabelProp,
    className,
    disableMultiRoom = !config.accommodation.allowsMultiRoom,
    onChange = noop,
  } = props

  const intl = useIntl()
  const childrenAgeLabel = childrenAgeLabelProp ?? intl.formatMessage({
    id: 'QGzLgEOsC92hg687A0C2',
    defaultMessage: 'Age at time of check-in',
  })

  const [localOccupancies, setLocalOccupancies] = useState<Array<App.Occupants | App.RoomOccupants>>(rooms)

  useEffect(() => {
    setLocalOccupancies(rooms)
  }, [rooms])

  const onOccupancyChange = useCallback((rooms: Array<App.Occupants | App.RoomOccupants>) => {
    setLocalOccupancies(rooms)
    onChange(rooms)
  }, [onChange])

  const childrenAgeThreshold = useMemo(() => {
    if (isBedbankOffer(offer)) {
      return getBedbankAndFlightsChildMaxAge(offer)
    } else if (isTourV2Offer(offer)) {
      if (isSalesforceTourV2(offer)) {
        return getTourV2OfferMinAge(offer)
      }
      return getTourV2OfferMaxChildAge(offer)
    } else if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxChildAge(offer)
    } else {
      return getOfferAndFlightsMaxChildAge(offer)
    }
  }, [offer])

  const isLETour = useMemo(() => (isTourV2Offer(offer) && isSalesforceTourV2(offer)), [offer])

  const infantAgeThreshold = useMemo(() => {
    if (isBundleOffer(offer)) {
      return getBundleAndSaveMaxInfantAge(offer)
    }
    return getOfferMaxInfantAge(offer)
  }, [offer])

  const teenagerAgeThreshold = useMemo(() => {
    return getOfferMaxTeenagerAge(offer)
  }, [offer])

  return (
    <OccupancyPickerVertical
      occupancies={localOccupancies}
      saleUnit={offer.saleUnit}
      maxChildAge={childrenAgeThreshold}
      maxInfantAge={infantAgeThreshold}
      maxTeenagerAge={teenagerAgeThreshold}
      capacities={capacities}
      data-testid="offer-capacity-with-ages-vertical-selection-dropdown"
      className={className}
      childrenAgeLabel={childrenAgeLabel}
      enableMultiRoom={!disableMultiRoom}
      adultLabelType={isLETour ? 'traveller' : 'adult'}
      showChildren={!isLETour}
      onChange={onOccupancyChange}
      offerType={offer.type}
    />
  )
}

export default withOfferPageState((state) => ({
  rooms: state.rooms,
  offer: state.offer ?? state.bedbankOffer ?? state.tourV2Offer,
  open: state.activeFilter === 'capacity',
}), OfferCapacityWithAgesVerticalSelection, preventRenderWithClosedFilter)
